import { useEffect, useRef, useState } from 'react'
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { serversApi } from '../api'
import { useAuthStore } from '../store/authStore'
import { useMonitoringCommand } from '../hooks/useMonitoringCommand'
import { SCOPE_EDGE_LICENSES, SCOPE_EDGE_SERVERS } from '../config/apps'
import { formatExpiry } from '../config/license'
import Toast from '../components/Toast'
import CloudLicenseModal from '../components/CloudLicenseModal'
import ServerEditModal from '../components/ServerEditModal'
import ActivationPanel from './serverDetail/ActivationPanel'
import type { DeviceIssue, EdgeServer, ServerCommandType } from '../types'
import DevicesTab from './serverDetail/DevicesTab'
import MasterDataTab from './serverDetail/MasterDataTab'
import NetworkTab from './serverDetail/network/NetworkTab'
import { TabButton } from './serverDetail/ui'
import { Lamp, type LampState } from '../components/Lamp'

// 상태 칩 — 램프 + 글자
function Badge({ lamp, children }: { lamp: LampState; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md border border-gray-200 bg-surface text-xs font-medium text-gray-700">
      <Lamp state={lamp} className="!w-[7px] !h-[7px]" />
      {children}
    </span>
  )
}

function formatDateTime(iso?: string) {
  return iso ? new Date(iso).toLocaleString('ko-KR', { hour12: false }) : '-'
}

// 자원 사용률 — 70% 이상 주황, 90% 이상 빨강
function Meter({ label, value }: { label: string; value?: number }) {
  const pct = value == null ? null : Math.round(value)
  const color = pct == null ? 'bg-gray-200' : pct >= 90 ? 'bg-lamp-fault' : pct >= 70 ? 'bg-lamp-warn' : 'bg-gray-500'
  return (
    <div className="min-w-[96px]">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="tabular font-medium text-gray-800">{pct == null ? '-' : `${pct}%`}</span>
      </div>
      <div className="w-full bg-gray-200/80 rounded-sm h-1 mt-1.5">
        <div className={`${color} h-1 rounded-sm transition-all`} style={{ width: `${pct ?? 0}%` }} />
      </div>
    </div>
  )
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-800 mt-0.5 truncate tabular">{children}</div>
    </div>
  )
}

// 엣지 DeviceMonitorStateType 의 Description
const DEVICE_STATE_LABELS: Record<string, string> = {
  Terminated: '연결 끊김', Disconnected: '연결 정지', Reconnecting: '재연결 중', Unknown: '상태 모름(UDP)', Connecting: '연결 중',
}

// 헤더 "디바이스" 칸 — 이상 개수를 누르면 어느 디바이스가 어떤 상태인지 펼친다 (heartbeat 의 deviceIssues).
// 모니터링 중지 중에는 엣지가 전부 미연결로 세므로 이상 대신 "모니터링 중지"로 보여준다
function DeviceStat({ server }: { server?: EdgeServer }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!server || (server.deviceConnectedCount == null && server.deviceNotConnectedCount == null))
    return <Stat label="디바이스">-</Stat>

  const total = (server.deviceConnectedCount ?? 0) + (server.deviceNotConnectedCount ?? 0)
  const stopped = server.reportedMonitoringState === 'Stopped'
  const issueCount = server.deviceNotConnectedCount ?? 0
  const issues: DeviceIssue[] | null | undefined = server.deviceIssues

  return (
    <div className="min-w-0 relative" ref={ref}>
      <div className="text-xs text-gray-500">디바이스</div>
      <div className="text-sm text-gray-800 mt-0.5 flex items-center gap-2">
        {total}
        {stopped ? (
          <span className="text-xs text-gray-400">모니터링 중지</span>
        ) : issueCount === 0 ? (
          <span className="text-xs text-gray-400">이상 0</span>
        ) : (
          <button onClick={() => setOpen(o => !o)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700">
            이상 {issueCount}
            <svg className={`w-3 h-3 transition ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
            </svg>
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 w-72 bg-surface rounded-xl border border-gray-200 shadow-lg py-2">
          <div className="px-4 pb-2 text-xs text-gray-400">연결되지 않은 디바이스</div>
          {issues == null ? (
            // 구버전 LiveEdge 는 개수만 보고한다
            <div className="px-4 py-2 text-xs text-gray-500">LiveEdge를 업데이트하면 디바이스별로 볼 수 있습니다.</div>
          ) : issues.length === 0 ? (
            <div className="px-4 py-2 text-xs text-gray-500">다음 하트비트에서 갱신됩니다.</div>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {issues.map(d => (
                <li key={d.code} className="flex items-center gap-3 px-4 py-1.5">
                  <Lamp state="fault" className="!w-1.5 !h-1.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-800 truncate">{d.name || d.code}</div>
                    <div className="text-[11px] text-gray-400 font-mono truncate">{d.code}</div>
                  </div>
                  <span className="text-xs text-red-600 shrink-0">{DEVICE_STATE_LABELS[d.state] ?? d.state}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// 원격 모니터링 시작/중지 — 대시보드 카드와 같은 규칙 (미보고·등록 미완료면 숨김, 오프라인이면 비활성)
function MonitoringButton({ server, pendingType, onCommand }: {
  server: EdgeServer
  pendingType?: ServerCommandType
  onCommand: (s: EdgeServer, type: ServerCommandType) => void
}) {
  if (!server.reportedMonitoringState || server.status !== 'Registered') return null

  if (pendingType) {
    // 완료(ServerCommandCompleted) 또는 60초 타임아웃까지 재클릭 차단
    return (
      <span className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
        <span className="inline-block w-3.5 h-3.5 border-2 border-gray-200 border-t-accent rounded-full animate-spin" />
        {pendingType === 'StopMonitoring' ? '중지 중…' : '시작 중…'}
      </span>
    )
  }

  const running = server.reportedMonitoringState === 'Running'
  return (
    <button
      onClick={() => onCommand(server, running ? 'StopMonitoring' : 'StartMonitoring')}
      disabled={!server.isOnline}
      title={server.isOnline ? undefined : 'Edge가 오프라인입니다'}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
        running
          ? 'border border-orange-200 text-orange-700 bg-surface hover:bg-orange-50'
          : 'bg-accent text-on-accent hover:bg-accent-hover'
      }`}>
      {running ? (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5" /></svg>
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" /></svg>
      )}
      {running ? '모니터링 중지' : '모니터링 시작'}
    </button>
  )
}

// 서버 상세: [디바이스] 엣지에 실제 설정된 디바이스·태그와 로우 데이터(태그 값) — Edge팀 전용
//           [마스터데이터] 태그로 만든 설비 신호(설정) + 그 결과인 설비 로그(실시간·조회)
//           [네트워크] 현장 네트워크 도면(스위치·변환기·배선·IP 대역) 실시간 공동 편집 — Edge팀 전용
type DetailTab = 'devices' | 'master' | 'network'

export default function ServerDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const [tab, setTab] = useState<DetailTab>('master')
  // 디바이스·태그, 마스터데이터 설정, 모니터링 제어는 Edge팀(Admin 또는 edge:servers)만 — API 의 ServersAdmin 정책과 동일
  const isEdgeTeam = useAuthStore(s => s.can(SCOPE_EDGE_SERVERS))
  const canLicenses = useAuthStore(s => s.can(SCOPE_EDGE_LICENSES))
  // 서버 정보 수정은 Manager(자기 회사) 또는 Edge팀 — API 의 ServersManage 정책과 동일
  const canEdit = useAuthStore(s => s.role === 'Manager') || isEdgeTeam
  const { pendingCommands, sendCommand, toast, setToast } = useMonitoringCommand(isEdgeTeam)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [licenseOpen, setLicenseOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [apiKeyCopied, setApiKeyCopied] = useState(false)

  // 상태·자원 사용량은 heartbeat 주기에 맞춰 폴링. 명령 완료 시에는 useMonitoringCommand 가 ['servers'] 를 무효화한다.
  // 등록 미완료(승인 대기·활성화 대기) 중에는 활성화되는 순간 화면을 넘기도록 더 자주 확인한다
  const { data: server } = useQuery({
    queryKey: ['servers', id],
    queryFn: () => serversApi.getById(id).then(r => r.data),
    enabled: !!id,
    refetchInterval: q => (q.state.data && q.state.data.status !== 'Registered' ? 5000 : 15000),
  })

  const deleteMutation = useMutation({
    mutationFn: () => serversApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] })
      qc.invalidateQueries({ queryKey: ['licenses'] })
      navigate('/', { replace: true })
    },
  })

  if (!id) return <Navigate to="/" replace />

  const registered = server?.status === 'Registered'

  const copyApiKey = () => {
    if (!server) return
    navigator.clipboard.writeText(server.apiKey)
    setApiKeyCopied(true)
    setTimeout(() => setApiKeyCopied(false), 2000)
  }

  // 마스터데이터 수정은 모니터링이 멈춰 있을 때만 — 수집 유닛이 시작 때 한 번 구성되어 실행 중 변경은 어긋난다.
  // 엣지도 같은 이유로 거부하므로 여기서는 미리 막고 이유를 보여주는 역할
  const lockReason = !server ? '서버 정보를 불러오는 중입니다.'
    : !server.isOnline ? 'Edge가 오프라인이라 수정할 수 없습니다.'
    : server.reportedMonitoringState === 'Running' ? '모니터링 중에는 수정할 수 없습니다. 먼저 모니터링을 중지하세요.'
    : null

  // 헤더·탭은 고정하고 탭 아래 컨텐츠만 스크롤 — Layout <main> 높이를 채워 main 자체는 스크롤되지 않게 한다
  return (
    <div className="flex flex-col h-full">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav className="flex items-center gap-1.5 text-[13px] text-gray-500 mb-3 shrink-0">
        {/* 서버 상세는 대시보드 카드에서만 들어온다 */}
        <Link to="/" className="hover:text-gray-700 transition">대시보드</Link>
        <span>/</span>
        <span className="text-gray-800 truncate">{server?.name ?? '…'}</span>
      </nav>

      <div className="bg-surface rounded-xl border border-gray-200 shrink-0">
        <div className="px-6 pt-5 pb-4 flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 truncate">{server?.name ?? '서버'}</h2>
              {server && (
                <Badge lamp={server.isOnline ? 'run' : 'off'}>{server.isOnline ? '온라인' : '오프라인'}</Badge>
              )}
              {/* 모니터링 중지 중에는 태그 값·설비 로그가 새로 오지 않는다 */}
              {server?.reportedMonitoringState === 'Running' && <Badge lamp="run">모니터링 중</Badge>}
              {server?.reportedMonitoringState === 'Stopped' && <Badge lamp="warn">모니터링 중지됨</Badge>}
              {server?.status === 'PendingApproval' && <Badge lamp="warn">승인 대기</Badge>}
              {server?.status === 'Issued' && <Badge lamp="warn">활성화 대기</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[13px] text-gray-500">
              {server?.company && <span className="text-gray-700">{server.company.name}</span>}
              <span className="font-mono">{server?.ipAddress ?? '-'}</span>
              {server?.memo && <span className="truncate max-w-xs" title={server.memo}>{server.memo}</span>}
              {registered && server?.licenseIssuance && (
                <span>라이센스 {formatExpiry(server.licenseIssuance.expiresAt)}까지, 노드 {server.licenseIssuance.maxNodeCount}개</span>
              )}
            </div>
            {/* 서버 관리 — 예전 Edge 서버 페이지의 행 동작을 상세로 옮겼다 */}
            {server && (
              <div className="flex flex-wrap items-center gap-4 mt-3 text-[13px]">
                {registered && (
                  <button onClick={copyApiKey} className="text-gray-500 hover:text-accent transition-colors">
                    {apiKeyCopied ? 'API Key 복사됨 ✓' : 'API Key 복사'}
                  </button>
                )}
                {registered && canLicenses && (
                  <button onClick={() => setLicenseOpen(true)} className="text-gray-500 hover:text-accent transition-colors">라이센스 변경</button>
                )}
                {canEdit && (
                  <button onClick={() => setEditOpen(true)} className="text-gray-500 hover:text-accent transition-colors">정보 수정</button>
                )}
                {server.status !== 'PendingApproval' && isEdgeTeam && (
                  <button
                    onClick={() => { if (confirm('서버를 삭제하시겠습니까?')) deleteMutation.mutate() }}
                    className="text-red-600/80 hover:text-red-600 transition-colors">서버 삭제</button>
                )}
              </div>
            )}
          </div>

          {isEdgeTeam && server && (
            <MonitoringButton server={server} pendingType={pendingCommands[server.id]} onCommand={sendCommand} />
          )}
        </div>

        {registered && (<>
          <div className="px-6 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3">
            <Meter label="CPU" value={server?.cpuUsage} />
            <Meter label="MEM" value={server?.memoryUsage} />
            <Meter label="DISK" value={server?.diskUsage} />
            <DeviceStat server={server} />
            <Stat label="마지막 데이터">{formatDateTime(server?.lastMachineLogAt)}</Stat>
            <Stat label="마지막 하트비트">{formatDateTime(server?.lastHeartbeat)}</Stat>
          </div>

          <div className="px-6 border-t border-gray-200">
            <div className="flex gap-6 pt-3">
              {isEdgeTeam && (
                <TabButton active={tab === 'devices'} onClick={() => setTab('devices')}>디바이스</TabButton>
              )}
              <TabButton active={tab === 'master'} onClick={() => setTab('master')}>마스터데이터</TabButton>
              {isEdgeTeam && (
                <TabButton active={tab === 'network'} onClick={() => setTab('network')}>네트워크</TabButton>
              )}
            </div>
          </div>
        </>)}
      </div>

      {/* -mx-1 px-1: overflow 로 카드 그림자가 좌우에서 잘리지 않게 */}
      <div className="flex-1 min-h-0 overflow-y-auto pt-5 pb-1 -mx-1 px-1">
        {server && !registered && (
          <ActivationPanel server={server} canIssue={canLicenses} canReject={isEdgeTeam}
            onReject={() => { if (confirm('등록 요청을 거절하시겠습니까?')) deleteMutation.mutate() }} />
        )}
        {registered && tab === 'devices' && isEdgeTeam && <DevicesTab serverId={id} lockReason={lockReason} />}
        {registered && tab === 'master' && <MasterDataTab serverId={id} canViewSettings={isEdgeTeam} lockReason={lockReason} />}
        {registered && tab === 'network' && isEdgeTeam && server && <NetworkTab server={server} />}
      </div>

      {licenseOpen && server && (
        <CloudLicenseModal server={server} current={server.licenseIssuance} onClose={() => setLicenseOpen(false)} />
      )}
      {editOpen && server && (
        <ServerEditModal server={server} canAssignCompany={isEdgeTeam} onClose={() => setEditOpen(false)} />
      )}
    </div>
  )
}
